// M7 – Popup tạo phiếu hoàn: chọn dòng + số lượng, tự tính tiền hoàn theo phần thực trả sau phân bổ (mục 2.6)
import { useEffect, useMemo, useState } from 'react';
import { Modal, Select, message } from 'antd';
import { AlertTriangle, Minus, Plus } from 'lucide-react';
import { METHOD_LABEL, METHODS_NEED_REF, type Order, type PaymentMethod, type RefundLine } from '../data';
import { refundLineAmount } from '../pricing';
import usePosStore from '../store';
import { money, usePosAuth } from '../utils';

const QUICK_REASONS = ['Khách không hài lòng dịch vụ', 'Khách đổi ý, chưa sử dụng', 'Sản phẩm lỗi / dị ứng', 'Thu ngân nhập sai đơn'];

interface Props { order: Order | null; onClose: () => void }

export default function RefundModal({ order, onClose }: Props) {
  const refunds = usePosStore(s => s.refunds);
  const transactions = usePosStore(s => s.transactions);
  const config = usePosStore(s => s.config);
  const workingBranch = usePosStore(s => s.workingBranch);
  const createRefund = usePosStore(s => s.createRefund);
  const { can, userName } = usePosAuth();

  const [qty, setQty] = useState<Record<string, number>>({});
  const [stock, setStock] = useState<Record<string, 'restock' | 'discard'>>({});
  const [method, setMethod] = useState<PaymentMethod>('transfer');
  const [txnRef, setTxnRef] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const pending = order ? refunds.find(r => r.orderId === order.id && (r.status === 'pending' || r.status === 'approved')) : undefined;
  const isToday = order ? new Date(order.createdAt).toDateString() === new Date().toDateString() : false;
  // BR7.3: hoàn tiền mặt chỉ cho đơn trong ngày, cùng chi nhánh
  const cashAllowed = isToday && order?.branchId === workingBranch;

  const paidMethods = useMemo(() => {
    if (!order) return [] as PaymentMethod[];
    const sums = new Map<PaymentMethod, number>();
    transactions.filter(t => t.orderId === order.id && t.amount > 0).forEach(t => sums.set(t.method, (sums.get(t.method) || 0) + t.amount));
    return Array.from(sums.entries()).sort((a, b) => b[1] - a[1]).map(([m]) => m);
  }, [order, transactions]);

  useEffect(() => {
    if (!order) return;
    setQty({});
    setStock({});
    const first = paidMethods.find(m => m !== 'cash' || cashAllowed) || 'transfer';
    setMethod(first);
    setTxnRef('');
    setReason('');
    setTouched(false);
  }, [order, paidMethods, cashAllowed]);

  if (!order) return null;

  const remainingQty = (key: string) => {
    const l = order.lines.find(x => x.key === key)!;
    return l.qty - l.refundedQty;
  };
  const maxRefund = order.paid - order.refunded;
  const chosen: RefundLine[] = order.lines
    .filter(l => (qty[l.key] || 0) > 0)
    .map(l => ({ lineKey: l.key, name: l.name, qty: qty[l.key], amount: refundLineAmount(l, qty[l.key]), ...(l.type === 'product' ? { stockAction: stock[l.key] || 'restock' } : {}) }));
  const total = chosen.reduce((s, l) => s + l.amount, 0);
  const allSelected = order.lines.every(l => remainingQty(l.key) === 0 || qty[l.key] === remainingQty(l.key));

  const needApproval = !can('approve') && (total >= config.refundApprovalThreshold || !isToday);
  const refRequired = !needApproval && METHODS_NEED_REF.includes(method);

  const errors: string[] = [];
  if (pending) errors.push(`Đơn đang có phiếu ${pending.id} chưa xử lý xong (R4)`);
  if (chosen.length === 0) errors.push('Chọn ít nhất 1 dòng để hoàn');
  if (total > maxRefund) errors.push(`Số tiền hoàn vượt quá số đã thu (tối đa ${money(maxRefund)}đ)`);
  if (method === 'cash' && !cashAllowed) errors.push('Tiền mặt chỉ hoàn cho đơn trong ngày tại chi nhánh đang làm việc');
  if (method === 'prepaid' && !order.customerId) errors.push('Khách lẻ không có ví trả trước');
  if (refRequired && !/^[A-Za-z0-9]{4,50}$/.test(txnRef)) errors.push('Nhập mã giao dịch hoàn (4–50 ký tự)');
  if (reason.trim().length < 10) errors.push('Lý do tối thiểu 10 ký tự');

  const toggleAll = (on: boolean) => {
    const next: Record<string, number> = {};
    order.lines.forEach(l => { if (on && remainingQty(l.key) > 0) next[l.key] = remainingQty(l.key); });
    setQty(next);
  };

  const submit = () => {
    setTouched(true);
    if (errors.length) return;
    const r = createRefund({ orderId: order.id, lines: chosen, method, txnRef, reason, by: userName, canApprove: can('approve') });
    message.success(r.status === 'pending' ? `Đã gửi phiếu ${r.id} chờ quản lý duyệt` : `Đã hoàn ${money(r.amount)}đ (${r.id})`);
    onClose();
  };

  return (
    <Modal open={!!order} onCancel={onClose} onOk={submit} width={820} centered destroyOnHidden
      title={`Hoàn tiền đơn ${order.id}`}
      okText={needApproval ? 'Gửi duyệt' : 'Hoàn ngay'} cancelText="Hủy"
      okButtonProps={{ size: 'large', danger: !needApproval }} cancelButtonProps={{ size: 'large' }}>
      <div className="space-y-4 pt-1">
        {pending && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Đơn đang có phiếu hoàn {pending.id} ({money(pending.amount)}đ) chưa xử lý xong. Mỗi đơn chỉ có 1 phiếu chờ tại một thời điểm.
          </div>
        )}
        <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700">
          <input type="checkbox" checked={allSelected && chosen.length > 0} onChange={e => toggleAll(e.target.checked)} className="h-5 w-5 accent-[#357f96]" /> Hoàn toàn bộ
        </label>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Dòng hàng</th>
                <th className="px-2 py-2 text-right">Đã mua</th>
                <th className="px-2 py-2 text-right">Đã hoàn</th>
                <th className="px-2 py-2 text-center">SL hoàn</th>
                <th className="px-3 py-2 text-right">Được hoàn</th>
                <th className="px-3 py-2 text-left">Kho</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map(l => {
                const rem = remainingQty(l.key);
                const q = qty[l.key] || 0;
                const set = (v: number) => setQty(prev => ({ ...prev, [l.key]: Math.max(0, Math.min(rem, v)) }));
                return (
                  <tr key={l.key} className={`border-t border-slate-100 ${rem === 0 ? 'opacity-50' : ''}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-800">{l.name}</div>
                      <div className="text-xs text-slate-500">Thực trả {money(l.net)}đ{l.allocated ? ` (đã trừ ${money(l.allocated)} giảm cấp đơn)` : ''}</div>
                    </td>
                    <td className="px-2 py-2 text-right">{l.qty}</td>
                    <td className="px-2 py-2 text-right">{l.refundedQty}</td>
                    <td className="px-2 py-2">
                      <div className="mx-auto flex h-10 w-32 items-center rounded-lg border border-slate-300">
                        <button disabled={rem === 0} onClick={() => set(q - 1)} className="flex h-full w-10 items-center justify-center text-slate-500 hover:bg-slate-100"><Minus className="h-4 w-4" /></button>
                        <span className="flex-1 text-center font-semibold">{q}</span>
                        <button disabled={rem === 0} onClick={() => set(q + 1)} className="flex h-full w-10 items-center justify-center text-slate-500 hover:bg-slate-100"><Plus className="h-4 w-4" /></button>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">{q ? money(refundLineAmount(l, q)) : '—'}</td>
                    <td className="px-3 py-2">
                      {l.type === 'product' ? (
                        <Select size="middle" disabled={!q} value={stock[l.key] || 'restock'} onChange={v => setStock(prev => ({ ...prev, [l.key]: v }))}
                          options={[{ value: 'restock', label: 'Nhập lại kho' }, { value: 'discard', label: 'Hủy hàng' }]} style={{ width: 130 }} />
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-4 py-3">
          <span className="text-sm text-slate-500">Tối đa được hoàn: <b className="text-slate-700">{money(maxRefund)}đ</b></span>
          <span className="text-base">Tổng hoàn: <b className="text-2xl text-violet-700">{money(total)}đ</b></span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Phương thức hoàn <span className="text-red-500">*</span></span>
            <Select size="large" className="w-full" value={method} onChange={setMethod}
              options={(['transfer', 'cash', 'card', 'ewallet', 'prepaid'] as PaymentMethod[]).map(m => ({
                value: m,
                disabled: (m === 'cash' && !cashAllowed) || (m === 'prepaid' && !order.customerId),
                label: `${METHOD_LABEL[m]}${paidMethods.includes(m) ? ' (đã thu)' : ''}${m === 'cash' && !cashAllowed ? ' – chỉ đơn hôm nay' : ''}`,
              }))} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Mã GD hoàn {refRequired && <span className="text-red-500">*</span>}</span>
            <input value={txnRef} maxLength={50} onChange={e => setTxnRef(e.target.value.toUpperCase().replace(/\s/g, ''))}
              placeholder={needApproval ? 'Nhập khi chi tiền sau khi được duyệt' : 'VD: FT26100800123'}
              disabled={method === 'cash' || method === 'prepaid'}
              className="h-10 w-full rounded-lg border border-slate-300 px-3 uppercase outline-none focus:border-[#3e91ab] focus:ring-2 focus:ring-[#3e91ab]/20 disabled:bg-slate-50" />
          </label>
        </div>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Lý do <span className="text-red-500">*</span></span>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {QUICK_REASONS.map(r => (
              <button key={r} type="button" onClick={() => setReason(r)} className="rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-600 hover:border-[#3e91ab] hover:text-[#2c6b7f]">{r}</button>
            ))}
          </div>
          <textarea value={reason} maxLength={500} rows={2} onChange={e => setReason(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-[#3e91ab] focus:ring-2 focus:ring-[#3e91ab]/20" />
          <span className="text-xs text-slate-400">{reason.trim().length}/500 · tối thiểu 10 ký tự</span>
        </label>
        {needApproval && total > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Cần quản lý duyệt vì {total >= config.refundApprovalThreshold ? `số tiền từ ${money(config.refundApprovalThreshold)}đ` : 'đơn không thuộc hôm nay'}. Sau khi duyệt, thu ngân chi tiền tại màn Hoàn tiền.
          </div>
        )}
        {touched && errors.length > 0 && (
          <ul className="list-disc space-y-0.5 pl-5 text-sm text-red-600">{errors.map(e => <li key={e}>{e}</li>)}</ul>
        )}
      </div>
    </Modal>
  );
}
